'use client';

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import {
  CaretDownIcon,
  CreditCardIcon,
  ReceiptIcon,
  WarningCircleIcon,
} from '@phosphor-icons/react';
import { PageHeader } from '@/components/PageHeader';
import { cn } from '@/lib/utils';
import { usePathname, useRouter, useSearchParams } from '@/lib/router';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { EChart, type EChartsOption } from '@/components/charts/EChart';
import { formatBRLCompact } from '@/lib/charts/formatters';
import { hideOverlapLabels } from '@/lib/charts/axis';
import { useIsMobile } from '@/hooks/use-mobile';
import { useThemeColors } from '@/lib/theme/useThemeColors';
import { token } from '@/lib/theme/tokens';
import { HorizontalScroller } from '@/components/ui/HorizontalScroller';
import { useFinance } from '@/hooks/useFinance';
import { usePeriodSelection } from '@/hooks/usePeriodSelection';
import { useCreditCockpit } from '@/hooks/queries/useCreditCockpit';
import { cardBrandImage, normalizeCardBrand } from '@/lib/card-brands';
import { getBankSurfaceColor } from '@/lib/bank-colors';
import { hashToCategoryColor } from '@/lib/transactions/categories';
import { fmt } from '@/lib/transactions/utils';
import {
  buildAccountCards,
  buildCommitmentChartData,
  deriveFaturasSummary,
  sortAccountCards,
  type AccountCard,
} from '@/components/faturas/insights';
import { ApiError } from '@/lib/api';
import type {
  CreditCardCockpitAccount,
  ExpenseTransaction,
  InvoiceDetailResponse,
} from '@/lib/api';
import type { Transaction } from '@/lib/transactions/types';
import { toast } from 'sonner';
import { notifyError } from '@/lib/notify-error';
import {
  appendInvoiceDetailPage,
  invoiceAfterReread,
  type InvoicePageRead,
  markInvoicePaidInCockpit,
  parseInvoiceDetailTarget,
  reloadInvoiceDetail,
  toInvoicePaidKey,
} from '@/components/transactions/screen/adapters';
import { useRereadOnSpaceWrite } from '@/lib/spaces/sendingChanges';
import { EstimateBadge } from '@/components/transactions/screen/EstimateBadge';
import { FaturaDetailDrawer } from '@/components/transactions/screen/FaturaDetailDrawer';
import { FaturaStatusBadge } from '@/components/transactions/screen/FaturaStatusBadge';
import { faturaEstimateKind, faturaEstimateSubline } from '@/components/transactions/screen/faturaEstimate';
import {
  type CategoryIndex,
  type CategoryMeta,
  UNCATEGORIZED_META,
} from '@/components/transactions/screen/categoryIndex';
import type { ScreenFatura } from '@/components/transactions/screen/types';
import { formatCalendarDatePtBr, shortCalendarMonthLabel } from './dates';
import { track } from '@/lib/telemetry';

type InstallmentLadderEntry = NonNullable<CreditCardCockpitAccount['installmentLadder']>[number];

const THUMB_SPARKLINE_WIDTH = 96;
const THUMB_SPARKLINE_HEIGHT = 28;

function formatDate(value?: string | null): string {
  return formatCalendarDatePtBr(value);
}

function formatDueDay(day: number | null): string {
  return day ? `Todo dia ${day}` : '--';
}

function buildThumbSparkline(
  ladder: InstallmentLadderEntry[],
  maxAmount: number,
) {
  const left = 4;
  const right = 4;
  const top = 4;
  const plotHeight = THUMB_SPARKLINE_HEIGHT - 8;
  const span = THUMB_SPARKLINE_WIDTH - left - right;
  return ladder.map((entry, index) => {
    const x = ladder.length === 1 ? THUMB_SPARKLINE_WIDTH / 2 : left + (span * index) / (ladder.length - 1);
    const y = top + (1 - entry.amount / Math.max(1, maxAmount)) * plotHeight;
    return { entry, x, y };
  });
}

// Usage reads the backend's reconciled figures (creditEffectiveLimit / creditUsedAmount) rather
// than deriving "used" as limit - available: when the cardholder caps their own limit in the
// bank's app, creditLimit is the APPROVED limit while creditAvailableLimit counts against the cap,
// and the subtraction reports the untouched headroom between them as spend.
function limitUsage(account: CreditCardCockpitAccount): number | null {
  if (!account.creditLimitReliable) return null;
  const limit = account.creditEffectiveLimit;
  const used = account.creditUsedAmount;
  if (!limit || limit <= 0 || used === null) return null;
  return Math.max(0, Math.min(1, used / limit));
}

/** Limit gauge for one card. Renders nothing when the bank's figures don't reconcile. */
function CardLimitUsage({
  account,
  accentColor,
}: Readonly<{
  account: CreditCardCockpitAccount;
  accentColor: string;
}>) {
  const usage = limitUsage(account);
  if (usage === null) return null;

  const usagePercent = Math.round(usage * 100);
  const used = account.creditUsedAmount;
  const effectiveLimit = account.creditEffectiveLimit;
  const approvedLimit = account.creditLimit;
  const isCapped = approvedLimit !== null && effectiveLimit !== null && effectiveLimit < approvedLimit;

  return (
    <div className="mt-4">
      <div className="mb-1 flex items-baseline justify-between gap-3 text-sm font-semibold text-text-secondary">
        <span>Uso do limite</span>
        <span className="font-mono text-sm font-bold tabular-nums" style={{ color: accentColor }}>
          {usagePercent}%
        </span>
      </div>
      <div className="h-2 overflow-hidden rounded-full bg-divider">
        <div
          className="h-full rounded-full"
          style={{ width: `${usagePercent}%`, backgroundColor: accentColor }}
        />
      </div>
      {used !== null && effectiveLimit !== null ? (
        <>
          <div className="mt-2 grid grid-cols-3 gap-2 text-xs font-semibold text-text-secondary">
            <span>
              Usado <strong className="block font-mono text-xs text-text-primary">{fmt(used)}</strong>
            </span>
            <span>
              Disponível{' '}
              <strong className="block font-mono text-xs text-text-primary">
                {account.creditAvailableLimit === null ? '--' : fmt(account.creditAvailableLimit)}
              </strong>
            </span>
            <span>
              Limite <strong className="block font-mono text-xs text-text-primary">{fmt(effectiveLimit)}</strong>
            </span>
          </div>
          {/* The cardholder capped their own limit in the bank's app — say so, otherwise the
              number here looks wrong next to the approved limit they were granted. */}
          {isCapped ? (
            <p className="mt-2 text-xs font-medium text-text-tertiary">
              Limite personalizado definido no app do banco. Limite aprovado: {fmt(approvedLimit)}.
            </p>
          ) : null}
        </>
      ) : null}
    </div>
  );
}

function cycleProgressDayLabel(fatura: ScreenFatura): string | null {
  if (!fatura.cycleStart || !fatura.cycleEnd) return null;

  const start = new Date(fatura.cycleStart);
  const end = new Date(fatura.cycleEnd);
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return null;

  const totalDays = Math.max(1, Math.ceil((end.getTime() - start.getTime()) / 86400000) + 1);
  const elapsedDays = Math.max(
    1,
    Math.min(totalDays, Math.round(fatura.cycleProgress * totalDays)),
  );
  return `${elapsedDays}/${totalDays} dias`;
}

function adaptExpenseTransaction(tx: ExpenseTransaction): Transaction {
  const categoryId = tx.categoryId || 'uncategorized';
  const parentCategoryId = tx.parentCategoryId || tx.categoryId || 'uncategorized';
  return {
    id: tx.id,
    date: tx.referenceDate ?? tx.effectiveDate ?? tx.date,
    merchant: tx.aiRefinement ?? tx.description,
    isExpected: tx.isExpected,
    amount: tx.isRefund ? Math.abs(tx.value) : -Math.abs(tx.value),
    accountId: tx.accountId,
    categoryId,
    parentCategoryId,
    paymentType: tx.method,
    installment: tx.parcelaInfo
      ? { current: tx.parcelaInfo.current, total: tx.parcelaInfo.total }
      : undefined,
    spaceExpenses: tx.spaceExpenses?.length ? tx.spaceExpenses : undefined,
  };
}

function categoryIndexFromDetail(detail: InvoiceDetailResponse | null): CategoryIndex {
  const idx = new Map<string, CategoryMeta>();
  if (detail) {
    for (const tx of detail.transactions.items) {
      const id = tx.categoryId || 'uncategorized';
      if (!idx.has(id)) {
        idx.set(id, {
          id,
          label: tx.categoryTranslated || tx.effectiveCategory || tx.category || tx.parentCategoryTranslated || tx.parentCategory || 'Outros',
          iconName: tx.categoryIcon || tx.parentCategoryIcon || 'circle-dashed',
          color: hashToCategoryColor(id),
        });
      }
      const parentId = tx.parentCategoryId || tx.categoryId || 'uncategorized';
      if (!idx.has(parentId)) {
        idx.set(parentId, {
          id: parentId,
          label: tx.parentCategoryTranslated || tx.parentCategory || tx.categoryTranslated || tx.effectiveCategory || 'Outros',
          iconName: tx.parentCategoryIcon || tx.categoryIcon || 'circle-dashed',
          color: hashToCategoryColor(parentId),
        });
      }
    }
  }
  idx.set(UNCATEGORIZED_META.id, UNCATEGORIZED_META);
  return idx;
}

export function FaturasScreen() {
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const { value: period } = usePeriodSelection();
  const { fetchInvoiceDetail, markClosedInvoicePaid } = useFinance();
  const cockpitRequest = useCreditCockpit(period);
  const cockpit = cockpitRequest.data ?? null;
  const isLoading = cockpitRequest.isLoading && !cockpit;
  const error = cockpitRequest.error ? 'Não foi possível carregar as faturas.' : null;
  const [selectedFaturaId, setSelectedFaturaId] = useState<string | null>(null);
  const [detail, setDetail] = useState<InvoiceDetailResponse | null>(null);
  const [isDetailLoading, setIsDetailLoading] = useState(false);
  const [isDetailLoadingMore, setIsDetailLoadingMore] = useState(false);
  const [isMarkingPaid, setIsMarkingPaid] = useState(false);
  const INVOICE_PAGE_SIZE = 50;

  const accountCards = useMemo<AccountCard[]>(() => {
    return sortAccountCards(buildAccountCards(cockpit));
  }, [cockpit]);

  const faturaById = useMemo(() => {
    const map = new Map<string, ScreenFatura>();
    for (const card of accountCards) {
      for (const fatura of card.faturas) map.set(fatura.id, fatura);
    }
    return map;
  }, [accountCards]);

  const selectedFatura = selectedFaturaId ? faturaById.get(selectedFaturaId) ?? null : null;

  // Track opens on the ID transition — `selectedFatura`'s object identity
  // churns on every cockpit revalidation while the drawer stays open.
  const lastTrackedFaturaId = useRef<string | null>(null);
  useEffect(() => {
    if (selectedFaturaId && selectedFaturaId !== lastTrackedFaturaId.current) {
      track('action.fatura.open_detail', { source: 'invoices_page' });
    }
    lastTrackedFaturaId.current = selectedFaturaId;
  }, [selectedFaturaId]);
  const invoiceDetailAccount = searchParams.get('view') === 'invoice_detail'
    ? searchParams.get('invoiceAccount')
    : null;
  const invoiceDetailPosition = searchParams.get('invoicePosition') ?? 'opening';

  useEffect(() => {
    if (!invoiceDetailAccount || faturaById.size === 0) return;
    const match = [...faturaById.values()].find((fatura) =>
      fatura.accountId === invoiceDetailAccount &&
      fatura.source.position === invoiceDetailPosition &&
      fatura.source.isOpen);
    if (match && selectedFaturaId !== match.id) setSelectedFaturaId(match.id);
  }, [faturaById, invoiceDetailAccount, invoiceDetailPosition, selectedFaturaId]);

  const clearInvoiceDetailQuery = useCallback(() => {
    if (searchParams.get('view') !== 'invoice_detail') return;
    const params = new URLSearchParams(searchParams.toString());
    params.delete('view');
    params.delete('invoiceAccount');
    params.delete('invoicePosition');
    const qs = params.toString();
    router.replace(qs ? `${pathname}?${qs}` : pathname, { scroll: false });
  }, [pathname, router, searchParams]);

  // A space write changes what a charge says it was sent to (`spaceExpenses`, VER-571): the open invoice reads the
  // charges it shows again, in place, unless a newer reread or another read of the invoice landed meanwhile.
  const { wrote: wroteDetail, reading: readingDetail } = useRereadOnSpaceWrite((landed) => {
    if (!selectedFatura) return;
    const { accountId, source } = selectedFatura;
    const target = parseInvoiceDetailTarget(selectedFatura);
    const readPage: InvoicePageRead = (page) =>
      fetchInvoiceDetail(accountId, target.year, target.month, source.position, page);
    reloadInvoiceDetail(readPage, detail, INVOICE_PAGE_SIZE)
      .then((res) => {
        if (landed()) setDetail((current) => invoiceAfterReread(current, res));
      })
      .catch((err) => console.error('Invoice detail reload failed', err));
  });

  useEffect(() => {
    wroteDetail();
    if (!selectedFatura) {
      setDetail(null);
      setIsDetailLoading(false);
      return;
    }
    let alive = true;
    const target = parseInvoiceDetailTarget(selectedFatura);
    setDetail(null);
    setIsDetailLoading(true);
    const settle = readingDetail();
    fetchInvoiceDetail(
      selectedFatura.accountId,
      target.year,
      target.month,
      selectedFatura.source.position,
      { limit: INVOICE_PAGE_SIZE, offset: 0 },
    )
      .then((res) => {
        if (!alive) return;
        settle();
        setDetail(res);
      })
      // The detail was cleared when the read started; a reread that landed since keeps what it brought.
      .catch((err) => console.error('Invoice detail load failed', err))
      .finally(() => {
        if (alive) setIsDetailLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [fetchInvoiceDetail, readingDetail, selectedFatura, wroteDetail]);

  const detailTransactions = useMemo(
    () => detail?.transactions.items.map(adaptExpenseTransaction) ?? [],
    [detail],
  );
  const categoryIndex = useMemo(() => categoryIndexFromDetail(detail), [detail]);

  const handleLoadMoreDetail = async () => {
    const nextOffset = detail?.transactions.nextOffset;
    if (!selectedFatura || !detail?.transactions.hasMore || nextOffset == null) return;
    const target = parseInvoiceDetailTarget(selectedFatura);
    setIsDetailLoadingMore(true);
    const settle = readingDetail();
    try {
      const next = await fetchInvoiceDetail(
        selectedFatura.accountId,
        target.year,
        target.month,
        selectedFatura.source.position,
        { limit: INVOICE_PAGE_SIZE, offset: nextOffset },
      );
      settle();
      setDetail((current) => appendInvoiceDetailPage(current, next));
    } catch (err) {
      console.error('Invoice detail next page load failed', err);
    } finally {
      setIsDetailLoadingMore(false);
    }
  };

  const handleMarkFaturaPaid = async (faturaId: string) => {
    const fatura = faturaById.get(faturaId);
    // Mirrors the drawer's gate: only closed cycles can be confirmed as paid. An
    // estimated bill is already settled by the detected payment (the backend
    // rejects it with 409 anyway).
    if (!fatura || fatura.source.isOpen || fatura.source.isEstimated || isMarkingPaid) return;

    const target = parseInvoiceDetailTarget(fatura);
    setIsMarkingPaid(true);
    try {
      await markClosedInvoicePaid(
        fatura.accountId,
        target.year,
        target.month,
        fatura.source.dueDate ?? fatura.dueDate,
        fatura.source.chargeFingerprint ?? '',
      );
      // Optimistic flip; the follow-up revalidation reads the live cockpit, which
      // already reflects the persisted override.
      const key = toInvoicePaidKey(fatura);
      await cockpitRequest.mutate(
        (current) => current ? markInvoicePaidInCockpit(current, key) : current,
        { revalidate: false },
      );
      setSelectedFaturaId(null);
      clearInvoiceDetailQuery();
      toast.success('Fatura marcada como paga');
      void cockpitRequest.mutate();
    } catch (err) {
      // 409: charges synced after the drawer loaded — refresh so the user sees them.
      const conflict = err instanceof ApiError && err.status === 409;
      if (conflict) void cockpitRequest.mutate();
      notifyError('faturas', conflict ? null : err, conflict
        ? 'A fatura mudou desde que você a abriu. Revise os lançamentos e tente novamente.'
        : 'Não foi possível marcar a fatura como paga');
    } finally {
      setIsMarkingPaid(false);
    }
  };

  const summary = useMemo(() => {
    return deriveFaturasSummary(accountCards);
  }, [accountCards]);

  // Open fatura(s) leading + future-parcelas tail — see buildCommitmentChartData.
  const commitmentChartData = useMemo(() => buildCommitmentChartData(cockpit), [cockpit]);

  return (
    <div data-tokens="v2" className="space-y-5 pb-20">
      <PageHeader
        title="Faturas"
        description="Cartões de crédito, vencimentos e parcelas futuras."
        titleClassName="font-display text-2xl font-semibold tracking-normal sm:text-3xl"
      />

      <FaturasHeroBand summary={summary} ladder={cockpit?.installmentLadder ?? []} />

      {isLoading && !cockpit ? (
        <FaturasSkeleton />
      ) : error ? (
        <StatePanel
          icon={<WarningCircleIcon className="size-5 text-error" weight="duotone" />}
          title={error}
          actionLabel="Tentar novamente"
          onAction={() => {
            void cockpitRequest.mutate();
          }}
        />
      ) : accountCards.length === 0 ? (
        <StatePanel
          icon={<ReceiptIcon className="size-5 text-text-tertiary" weight="duotone" />}
          title="Nenhuma fatura no período"
        />
      ) : (
        <>
          {commitmentChartData.length > 0 ? (
            <AggregateInstallmentsChart data={commitmentChartData} />
          ) : null}
          <ul className="grid min-w-0 gap-4 min-[880px]:grid-cols-2">
            {accountCards.map((card) => (
              <FaturaAccountPanel
                key={card.account.id}
                card={card}
                onOpenFatura={setSelectedFaturaId}
              />
            ))}
          </ul>
        </>
      )}

      <FaturaDetailDrawer
        open={!!selectedFaturaId}
        onOpenChange={(open) => {
          setSelectedFaturaId(open ? selectedFaturaId : null);
          if (!open) clearInvoiceDetailQuery();
        }}
        fatura={selectedFatura}
        transactions={detailTransactions}
        categoryIndex={categoryIndex}
        onMarkAsPaid={handleMarkFaturaPaid}
        isMarkingPaid={isMarkingPaid}
        totalCount={detail?.transactions.total ?? selectedFatura?.transactionCount ?? detailTransactions.length}
        hasMore={Boolean(detail?.transactions.hasMore)}
        isLoading={isDetailLoading}
        isLoadingMore={isDetailLoadingMore}
        onLoadMore={handleLoadMoreDetail}
      />
    </div>
  );
}

// Splits an amount so the cents render smaller + muted (e.g. "R$ 6.581" big, ",33" small grey).
// Font is caller-specified: sans for big headline numbers, mono for small/secondary figures.
function Amount({
  value,
  className,
  decimalClassName,
}: {
  value: string;
  className?: string;
  decimalClassName?: string;
}) {
  const comma = value.lastIndexOf(',');
  const intPart = comma === -1 ? value : value.slice(0, comma);
  const decPart = comma === -1 ? null : value.slice(comma);
  return (
    <span className={cn('tabular-nums', className)}>
      {intPart}
      {decPart ? <span className={cn('text-text-disabled', decimalClassName)}>{decPart}</span> : null}
    </span>
  );
}

function StatCell({ label, value, valueClassName }: { label: string; value: string; valueClassName?: string }) {
  return (
    <div className="min-w-0 bg-surface px-3 py-2.5">
      <p className="truncate font-mono text-3xs font-semibold tracking-[0.08em] text-text-tertiary uppercase">{label}</p>
      <p className={cn('mt-1.5 truncate font-mono text-sm font-medium text-text-primary tabular-nums', valueClassName)}>
        {value}
      </p>
    </div>
  );
}

function FaturasHeroBand({
  summary,
  ladder,
}: {
  summary: ReturnType<typeof deriveFaturasSummary>;
  ladder: InstallmentLadderEntry[];
}) {
  const limitPct = summary.limitUsedPct === null ? null : Math.round(summary.limitUsedPct * 100);
  const radialAccent = summary.nextDueAccentColor ?? token('--primary', '#0E5A4A');
  const dueDays = summary.nextDueDays;
  const dueLabel =
    dueDays === null
      ? null
      : dueDays === 0
      ? 'vence hoje'
      : dueDays === 1
      ? 'vence amanhã'
      : `vence em ${dueDays} dias`;
  const futureTotal = ladder.reduce((sum, entry) => sum + entry.amount, 0);
  const deltaPct = summary.periodDeltaPct;
  const cardCountLabel =
    summary.activeCardCount === 0
      ? null
      : summary.activeCardCount === 1
      ? '1 cartão'
      : `${summary.activeCardCount} cartões`;

  return (
    <section className="rounded-[var(--radius-3xl)] border border-border bg-surface p-5 shadow-[var(--shadow-1)] sm:p-6">
      <div className="grid gap-5 min-[880px]:grid-cols-[1.5fr_1.7fr] min-[880px]:items-center">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <p className="font-mono text-3xs font-semibold tracking-[0.1em] text-text-tertiary uppercase">Em aberto</p>
            {deltaPct !== null ? <TrendChip deltaPct={deltaPct} /> : null}
          </div>
          <Amount
            value={fmt(summary.openTotal)}
            className="mt-2 block truncate font-sans text-[34px] leading-none font-semibold text-text-primary sm:text-[40px]"
            decimalClassName="text-xl sm:text-2xl"
          />
          <p className="mt-3 text-sm font-medium text-text-secondary">
            {cardCountLabel ? <span>{cardCountLabel}</span> : null}
            {cardCountLabel && dueLabel ? <span className="text-text-tertiary"> · </span> : null}
            {dueLabel ? (
              <span>
                próxima vence{' '}
                <span className="font-semibold text-warning-text">{dueLabel.replace(/^vence /, '')}</span>
                {summary.nextDueAccountName ? ` (${summary.nextDueAccountName})` : ''}
              </span>
            ) : null}
            {!cardCountLabel && !dueLabel ? 'Sem faturas em aberto no período.' : null}
          </p>
          {summary.openTotal !== summary.total ? (
            <p className="mt-1.5 text-xs font-medium text-text-tertiary">
              Total no período <Amount value={fmt(summary.total)} className="font-mono text-text-secondary" decimalClassName="text-text-disabled" />
            </p>
          ) : null}
        </div>

        <div className="grid grid-cols-2 gap-5 min-[880px]:gap-0">
          <LimitUsedRadial pct={limitPct} accentColor={radialAccent} />
          <FutureCommitmentsThumb total={futureTotal} ladder={ladder} />
        </div>
      </div>
    </section>
  );
}

function TrendChip({ deltaPct }: { deltaPct: number }) {
  const rounded = Math.round(deltaPct * 10) / 10;
  if (rounded === 0) {
    return (
      <span className="rounded-full bg-surface-sunken px-2 py-0.5 text-2xs font-bold text-text-secondary tabular-nums">
        = 0%
      </span>
    );
  }
  // Up = bad for expenses, down = good (same rule as components/StatCard.tsx).
  const isUp = rounded > 0;
  const cls = isUp ? 'bg-error-surface text-error-text' : 'bg-success-surface text-success-text';
  return (
    <span className={`rounded-full px-2 py-0.5 text-2xs font-bold tabular-nums ${cls}`}>
      {isUp ? '↑' : '↓'} {Math.abs(rounded).toFixed(1)}% vs ciclo anterior
    </span>
  );
}

function LimitUsedRadial({ pct, accentColor }: { pct: number | null; accentColor: string }) {
  const value = pct ?? 0;
  const size = 64;
  const stroke = 8;
  const radius = (size - stroke) / 2;
  const circumference = 2 * Math.PI * radius;
  const dashOffset = circumference * (1 - value / 100);
  return (
    <div className="flex min-w-0 flex-col items-center justify-center gap-2 min-[880px]:border-l min-[880px]:border-divider min-[880px]:px-4">
      <div className="relative size-24">
        <svg
          role="img"
          aria-label={pct === null ? 'Uso do limite indisponivel' : `Uso do limite ${pct}%`}
          viewBox={`0 0 ${size} ${size}`}
          className="size-full -rotate-90"
        >
          <circle
            cx={size / 2}
            cy={size / 2}
            r={radius}
            className="stroke-border-strong"
            strokeWidth={stroke}
            fill="none"
          />
          {pct === null ? null : (
            <circle
              cx={size / 2}
              cy={size / 2}
              r={radius}
              stroke={accentColor}
              strokeWidth={stroke}
              strokeLinecap="round"
              strokeDasharray={circumference}
              strokeDashoffset={dashOffset}
              fill="none"
            />
          )}
        </svg>
        <div className="absolute inset-0 flex items-center justify-center">
          <span className="text-lg font-bold text-text-primary tabular-nums">{pct === null ? '--' : `${pct}%`}</span>
        </div>
      </div>
      <p className="font-mono text-3xs font-semibold tracking-[0.1em] text-text-tertiary uppercase">
        Limite usado
      </p>
    </div>
  );
}

function FutureCommitmentsThumb({
  total,
  ladder,
}: {
  total: number;
  ladder: InstallmentLadderEntry[];
}) {
  const maxAmount = Math.max(1, ...ladder.map((entry) => entry.amount));
  const points = buildThumbSparkline(ladder, maxAmount);
  const polyline = points.map((point) => `${point.x},${point.y}`).join(' ');
  const area = points.length
    ? `M ${points[0].x} ${THUMB_SPARKLINE_HEIGHT} L ${points
        .map((point) => `${point.x} ${point.y}`)
        .join(' L ')} L ${points[points.length - 1].x} ${THUMB_SPARKLINE_HEIGHT} Z`
    : '';

  return (
    <div className="flex min-w-0 flex-col justify-center min-[880px]:border-l min-[880px]:border-divider min-[880px]:pl-5">
      <p className="font-mono text-3xs font-semibold tracking-[0.1em] text-text-tertiary uppercase">
        Parcelas futuras
      </p>
      <Amount value={fmt(total)} className="mt-1.5 font-mono text-xl font-medium text-text-primary" decimalClassName="text-sm" />
      {points.length > 0 ? (
        <svg
          role="img"
          aria-label="Tendencia de parcelas futuras"
          viewBox={`0 0 ${THUMB_SPARKLINE_WIDTH} ${THUMB_SPARKLINE_HEIGHT}`}
          className="mt-1 h-7 w-full overflow-visible"
          preserveAspectRatio="none"
        >
          <path d={area} fill="currentColor" className="text-success" opacity="0.15" />
          <polyline
            points={polyline}
            fill="none"
            stroke="currentColor"
            className="text-success"
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2"
          />
        </svg>
      ) : (
        <p className="mt-1 text-2xs font-semibold text-text-secondary">Sem compromissos.</p>
      )}
    </div>
  );
}

function AggregateInstallmentsChart({ data }: { data: InstallmentLadderEntry[] }) {
  const theme = useThemeColors();
  const isMobile = useIsMobile();
  const option = useMemo<EChartsOption>(() => {
    const axisLabel = { color: theme.textSecondary, fontFamily: 'DM Mono, ui-monospace, monospace', fontSize: 10 } as const;
    return {
      // Extra side room so the first/last points' value labels aren't clipped by
      // the edges (containLabel only reserves for axis labels, not series labels).
      grid: { left: 20, right: 64, top: 34, bottom: 6, containLabel: true },
      tooltip: {
        trigger: 'axis',
        valueFormatter: (value) => fmt(Number(value)),
      },
      xAxis: {
        type: 'category',
        data: data.map((entry) => entry.monthLabel),
        boundaryGap: false,
        axisLine: { show: false },
        axisTick: { show: false },
        axisLabel: { ...axisLabel, margin: 12 },
      },
      yAxis: {
        type: 'value',
        splitNumber: 3,
        axisLabel: { ...axisLabel, margin: 10, formatter: (value: number) => formatBRLCompact(value) },
        splitLine: { lineStyle: { color: theme.border } },
      },
      series: [
        {
          type: 'line',
          data: data.map((entry) => entry.amount),
          smooth: true,
          symbol: 'circle',
          symbolSize: 7,
          // Surface ring so the dot stays legible where it sits on the line.
          itemStyle: { color: theme.primary, borderColor: theme.surface, borderWidth: 2 },
          lineStyle: { color: theme.primary, width: 2 },
          areaStyle: {
            color: {
              type: 'linear',
              x: 0, y: 0, x2: 0, y2: 1,
              colorStops: [
                { offset: 0, color: 'rgba(14,90,74,0.12)' },
                { offset: 1, color: 'rgba(14,90,74,0)' },
              ],
            },
          },
          label: {
            show: true,
            position: 'top',
            distance: 8,
            color: theme.textSecondary,
            fontFamily: 'DM Mono, ui-monospace, monospace',
            // Smaller on mobile where space is tight, so more values survive the
            // overlap-thinning below; desktop gets the larger, more legible size.
            fontSize: isMobile ? 10 : 12,
            // Surface-colored chip behind the value: on mobile's compressed plot a
            // steep segment or a gridline routinely passes behind a label — the chip
            // masks the crossing stroke (including between glyphs, which a text halo
            // can't cover) so the value stays legible instead of being overprinted.
            backgroundColor: theme.surface,
            padding: [2, 4],
            formatter: (params) => fmt(Number(params.value)),
          },
          // Show as many value labels as fit; drop only the ones that would collide
          // with a neighbour, so nothing ever overprints on either viewport.
          labelLayout: hideOverlapLabels,
        },
      ],
    };
  }, [data, theme, isMobile]);

  return (
    <section className="rounded-[var(--radius-3xl)] border border-border bg-surface p-5 shadow-[var(--shadow-1)] sm:p-6">
      <div className="mb-3 flex items-baseline justify-between gap-3">
        <p className="font-mono text-3xs font-semibold tracking-[0.1em] text-text-tertiary uppercase">
          Compromisso por mês
        </p>
        <p className="text-2xs font-medium text-text-tertiary">Soma agregada dos cartões</p>
      </div>
      <div className="h-72 w-full sm:h-80">
        <EChart option={option} className="size-full" />
      </div>
    </section>
  );
}

function AccountInstallmentLineChart({
  data,
  accentColor,
}: {
  data: InstallmentLadderEntry[];
  accentColor: string;
}) {
  const maxAmount = Math.max(1, ...data.map((entry) => entry.amount));
  const left = 34;
  const right = 26;
  const top = 20;
  const plotHeight = 34;
  const baseline = 64;
  const step = 112;
  const chartWidth = Math.max(260, left + right + Math.max(0, data.length - 1) * step);
  const points = data.map((entry, index) => {
    const x = left + index * step;
    const y = top + (1 - entry.amount / maxAmount) * plotHeight;
    return { entry, x, y };
  });
  const polyline = points.map((point) => `${point.x},${point.y}`).join(' ');
  const area = points.length
    ? `M ${points[0].x} ${baseline} L ${points
        .map((point) => `${point.x} ${point.y}`)
        .join(' L ')} L ${points[points.length - 1].x} ${baseline} Z`
    : '';

  return (
    <HorizontalScroller
      ariaLabelLeft="Parcelas futuras anteriores"
      ariaLabelRight="Próximas parcelas futuras"
      className="rounded-xl bg-surface-sunken"
      gapClassName="gap-0"
      paddingBottomClassName="pb-3"
    >
      <div className="shrink-0 p-2" style={{ width: chartWidth }}>
        <svg
          role="img"
          aria-label="Série temporal de parcelas futuras deste cartão"
          viewBox={`0 0 ${chartWidth} 84`}
          className="h-24 w-full overflow-visible"
        >
          <path d={area} fill={accentColor} opacity="0.1" />
          <polyline
            points={polyline}
            fill="none"
            stroke={accentColor}
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2.8"
          />
          {points.map((point) => (
            <g key={point.entry.month}>
              <circle cx={point.x} cy={point.y} r="3.5" className="fill-surface" stroke={accentColor} strokeWidth="2.2" />
              <text
                x={point.x}
                y={Math.max(8, point.y - 8)}
                textAnchor="middle"
                fontSize="8"
                fontWeight="700"
                className="fill-text-primary"
              >
                {fmt(point.entry.amount)}
              </text>
              <text
                x={point.x}
                y="78"
                textAnchor="middle"
                fontSize="10"
                fontWeight="700"
                className="fill-text-tertiary"
              >
                {point.entry.monthLabel}
              </text>
            </g>
          ))}
        </svg>
      </div>
    </HorizontalScroller>
  );
}

function FaturaAccountPanel({
  card,
  onOpenFatura,
}: {
  card: AccountCard;
  onOpenFatura: (faturaId: string) => void;
}) {
  const latest = card.faturas[card.faturas.length - 1] ?? null;
  const cyclePercent = latest ? Math.round(latest.cycleProgress * 100) : null;
  const cycleDays = latest ? cycleProgressDayLabel(latest) : null;
  const accentColor = latest?.bankColor ?? token('--primary', '#059669');
  const accentSurface = getBankSurfaceColor(accentColor, 'tinted');
  const ladder = card.account.installmentLadder ?? [];
  const image = latest?.connectorImage ?? card.account.image;
  const last4 = card.account.number?.replace(/\D/g, '').slice(-4);
  const brand = normalizeCardBrand(card.account.creditBrand ?? latest?.creditBrand ?? null);
  const brandSrc = brand ? cardBrandImage(brand) : null;
  const [isExpanded, setIsExpanded] = useState(false);
  const detailsId = `faturas-card-details-${card.account.id.replace(/[^a-zA-Z0-9_-]/g, '-')}`;

  return (
    <li className="min-w-0 list-none overflow-hidden rounded-[var(--radius-3xl)] border border-border bg-surface p-5 shadow-[var(--shadow-1)] sm:p-6">
      <div className="flex items-start justify-between gap-3">
        <div className="flex min-w-0 items-center gap-3">
          <span
            className="flex size-10 shrink-0 items-center justify-center overflow-hidden rounded-xl"
            style={{ backgroundColor: accentSurface, color: accentColor }}
          >
            {image ? (
              <img src={image} alt="" className="size-full object-cover" />
            ) : (
              <CreditCardIcon className="size-5" weight="duotone" />
            )}
          </span>
          <div className="min-w-0">
            <div className="flex min-w-0 items-center gap-2">
              <p className="truncate text-sm font-semibold text-text-primary">{card.account.name}</p>
              {brandSrc ? (
                <img
                  src={brandSrc}
                  alt={brand ?? 'Bandeira do cartão'}
                  className="h-4 w-auto shrink-0"
                  onError={(event) => { event.currentTarget.style.display = 'none'; }}
                />
              ) : null}
            </div>
            <p className="truncate text-xs font-medium text-text-secondary">
              {last4 ? `Final ${last4}` : brand ? brand.toUpperCase() : 'Cartão de crédito'}
            </p>
          </div>
        </div>
      </div>

      <div className="mt-4 flex items-end justify-between gap-3">
        <div>
          <p className="font-mono text-3xs font-semibold tracking-[0.1em] text-text-tertiary uppercase">Em aberto</p>
          <Amount value={fmt(card.openTotal)} className="mt-1 block font-sans text-2xl font-semibold text-text-primary" decimalClassName="text-sm" />
        </div>
        <div className="text-right">
          <p className="font-mono text-3xs font-semibold tracking-[0.1em] text-text-tertiary uppercase">Vencimento</p>
          <p className="mt-1 text-sm font-bold text-text-primary">{formatDueDay(card.account.dueDay)}</p>
        </div>
      </div>

      <div className="mt-4 grid grid-cols-3 gap-px overflow-hidden rounded-[var(--radius-lg)] border border-divider bg-divider">
        <StatCell
          label="Disponível"
          value={card.account.creditAvailableLimit === null ? '--' : fmt(card.account.creditAvailableLimit)}
          valueClassName="text-success-text"
        />
        <StatCell
          label="Pagto mín."
          value={card.account.creditMinimumPayment === null ? '--' : fmt(card.account.creditMinimumPayment)}
        />
        <StatCell
          label="Próx. venc."
          value={card.nextDue ? formatDate(card.nextDue.dueDate) : '--'}
        />
      </div>

      <CardLimitUsage account={card.account} accentColor={accentColor} />

      {latest?.status === 'open' ? (
        <div className="mt-4">
          <div className="mb-1 flex items-baseline justify-between gap-3 text-sm font-semibold text-text-secondary">
            <span>Ciclo atual</span>
            <span className="text-right">
              <span className="font-mono text-sm font-bold tabular-nums" style={{ color: accentColor }}>
                {cyclePercent}%
              </span>
              {cycleDays ? <span className="ml-1 text-text-secondary">· {cycleDays}</span> : null}
            </span>
          </div>
          <div className="h-2 overflow-hidden rounded-full bg-divider">
            <div
              className="h-full rounded-full"
              style={{ width: `${cyclePercent}%`, backgroundColor: accentColor }}
            />
          </div>
        </div>
      ) : null}

      <button
        type="button"
        aria-expanded={isExpanded}
        aria-controls={detailsId}
        onClick={() => setIsExpanded((value) => !value)}
        className="mt-4 flex min-h-14 w-full items-center justify-between rounded-[var(--radius-lg)] border border-border bg-surface-sunken px-4 py-3 text-left text-sm font-semibold text-text-primary transition hover:bg-border sm:min-h-11 sm:px-3 sm:py-0"
      >
        <span className="min-w-0 truncate">{isExpanded ? 'Ocultar detalhes' : 'Ver faturas e parcelas'}</span>
        <span className="ml-3 flex shrink-0 items-center gap-2 text-sm font-semibold text-text-secondary">
          {card.faturas.length} fatura{card.faturas.length === 1 ? '' : 's'}
          <CaretDownIcon
            className={`size-5 text-text-tertiary transition-transform sm:size-4 ${isExpanded ? 'rotate-180' : ''}`}
            weight="bold"
          />
        </span>
      </button>

      {isExpanded ? (
        <div id={detailsId} className="mt-4 space-y-4">
          <div className="flex items-baseline justify-between gap-3">
            <p className="font-mono text-3xs font-semibold tracking-[0.1em] text-text-tertiary uppercase">
              Total selecionado
            </p>
            <Amount
              value={fmt(card.total)}
              className="font-mono text-sm font-semibold text-text-primary"
              decimalClassName="text-text-disabled"
            />
          </div>

          <div>
            <p className="mb-2 font-mono text-3xs font-semibold tracking-[0.1em] text-text-tertiary uppercase">
              Parcelas futuras neste cartão
            </p>
            {ladder.length > 0 ? (
              <AccountInstallmentLineChart data={ladder} accentColor={accentColor} />
            ) : (
              <p className="rounded-lg bg-surface-sunken px-3 py-2 text-xs font-medium text-text-secondary">
                Sem parcelas futuras.
              </p>
            )}
          </div>

      <div>
        <p className="mb-2 font-mono text-3xs font-semibold tracking-[0.1em] text-text-tertiary uppercase">
          Faturas deste cartão
        </p>
        <HorizontalScroller
          ariaLabelLeft="Faturas anteriores"
          ariaLabelRight="Próximas faturas"
          gapClassName="gap-2"
          paddingBottomClassName="pb-3"
        >
        {card.faturas.map((fatura) => {
          // Phantom-closed-bill window: the just-closed bill's transactions haven't been
          // associated yet, so this open invoice is a non-interactive "waiting on the bank"
          // placeholder — no figures, no drill-in.
          const awaiting = fatura.source.awaitingBillSync ?? false;
          // Platform-estimated invoice (detected bill payment, bank bill not delivered yet):
          // still drillable, but flagged and with the estimate's provenance as subline.
          const estimateSubline = faturaEstimateKind(fatura) ? faturaEstimateSubline(fatura) : null;
          const pillClass =
            'relative w-54 shrink-0 snap-start overflow-hidden rounded-xl border bg-surface-sunken p-3 text-left sm:w-58';
          const inner = (
            <>
              <span
                aria-hidden
                className="absolute inset-x-0 top-0 h-1"
                style={{ backgroundColor: fatura.bankColor }}
              />
              <div className="flex items-center justify-between gap-2">
                <span className="text-xs font-bold tracking-wider text-text-secondary uppercase">
                  {fatura.dueDate ? shortCalendarMonthLabel(fatura.dueDate) : fatura.source.label}
                </span>
                {awaiting ? (
                  <Badge size="sm" variant="info">Sincronizando</Badge>
                ) : estimateSubline ? (
                  <span className="inline-flex items-center gap-1">
                    <FaturaStatusBadge status={fatura.status} accentColor={fatura.bankColor} />
                    <EstimateBadge />
                  </span>
                ) : (
                  <FaturaStatusBadge status={fatura.status} accentColor={fatura.bankColor} />
                )}
              </div>
              <p className="mt-2 font-display text-base font-bold whitespace-nowrap text-text-primary tabular-nums">
                {awaiting ? '—' : fmt(fatura.total)}
              </p>
              {awaiting ? (
                <p className="mt-1 text-xs font-medium text-text-secondary">
                  Aguardando lançamentos do banco · vence {formatDate(fatura.dueDate)}
                </p>
              ) : estimateSubline ? (
                <p className="mt-1 text-xs font-medium text-text-secondary">
                  {estimateSubline}
                </p>
              ) : (
                <p className="mt-1 text-xs font-medium whitespace-nowrap text-text-secondary">
                  {fatura.transactionCount} lanc. · vence {formatDate(fatura.dueDate)}
                </p>
              )}
            </>
          );

          if (awaiting) {
            return (
              <div
                key={fatura.id}
                className={pillClass}
                style={{ borderColor: `${fatura.bankColor}33` }}
                aria-label="Fatura aguardando sincronização do banco"
              >
                {inner}
              </div>
            );
          }

          return (
            <button
              key={fatura.id}
              type="button"
              onClick={() => onOpenFatura(fatura.id)}
              className={`${pillClass} transition hover:bg-surface active:scale-[0.99]`}
              style={{ borderColor: `${fatura.bankColor}33` }}
            >
              {inner}
            </button>
          );
        })}
        </HorizontalScroller>
      </div>
        </div>
      ) : null}
    </li>
  );
}

function FaturasSkeleton() {
  return (
    <div className="grid gap-4 lg:grid-cols-2">
      {[0, 1].map((item) => (
        <div key={item} className="h-80 animate-pulse rounded-[var(--radius-3xl)] border border-border bg-surface p-4 shadow-[var(--shadow-1)]">
          <div className="h-10 w-40 rounded-xl bg-surface-sunken" />
          <div className="mt-6 h-8 w-32 rounded bg-surface-sunken" />
          <div className="mt-6 grid grid-cols-3 gap-2">
            <div className="h-14 rounded-lg bg-surface-sunken" />
            <div className="h-14 rounded-lg bg-surface-sunken" />
            <div className="h-14 rounded-lg bg-surface-sunken" />
          </div>
        </div>
      ))}
    </div>
  );
}

function StatePanel({
  icon,
  title,
  actionLabel,
  onAction,
}: {
  icon: ReactNode;
  title: string;
  actionLabel?: string;
  onAction?: () => void;
}) {
  return (
    <div className="rounded-[var(--radius-3xl)] border border-border bg-surface p-8 text-center shadow-[var(--shadow-1)]">
      <div className="mx-auto flex size-12 items-center justify-center rounded-full bg-surface-sunken">{icon}</div>
      <h3 className="mt-3 text-base font-bold text-text-primary">{title}</h3>
      {actionLabel && onAction ? (
        <Button type="button" variant="outline" size="sm" className="mt-4 rounded-xl" onClick={onAction}>
          {actionLabel}
        </Button>
      ) : null}
    </div>
  );
}
